import {Formik, Field} from 'formik';
import {object, string, bool, boolean, mixed} from 'yup';
import {FormikTextInput, FormikPhoneInput, FormikCheckbox, FormikSelect, DeprecatedButton, SvgIcon} from 'shipwell-ui';
import {states} from 'App/utils/globals';
import {countries} from 'App/utils/countries';
import {validatePhoneNumber} from 'App/components/forms/utils';
import FormFooter from 'App/formComponents/formSections/formFooter';
import './styles.scss';

/** Default FedEx Registration values */
export const defaultValues = {
  account_number: '',
  billing_address: {
    line_1: '',
    city: '',
    state_or_province: '',
    postal_code: '',
    country_code: ''
  },
  contact: {
    first_name: '',
    last_name: '',
    company_name: '',
    email_address: '',
    phone_number: ''
  },
  secondary_email: '',
  smart_post_hub_id: '',
  has_smartpost: false,
  accept_terms: false,
  has_freight_account: false
};

export const freightAccountSchema = object().shape({
  freight_account_number: string().required('Freight account number is required'),
  line_1: string().required('Street address is required.'),
  city: string().required('City is required.'),
  state_or_province: string().required('State is required.'),
  postal_code: string().required('Postal code is required.'),
  country_code: string().required('Country is required.')
});

/** FedEx Registration validation rules */
export const validationSchema = object().shape({
  account_number: string()
    .test(
      'account_number',
      'Account ID should be 8 or 9 digits.',
      (val) => val && (val.length === 8 || val.length === 9)
    )
    .required('Account ID is required.'),
  billing_address: object().shape({
    line_1: string().required('Street address is required.'),
    city: string().required('City is required.'),
    state_or_province: string().required('State is required.'),
    postal_code: string().required('Postal code is required.'),
    country_code: string().required('Country is required.')
  }),
  contact: object().shape({
    first_name: string().required('First name is required.'),
    last_name: string().required('Last name is required.'),
    company_name: string().required('Company is required.'),
    email_address: string().email('Enter a valid email.').required('Email is required.'),
    phone_number: string()
      .test('valid-phone', 'Enter a valid phone.', (value) => {
        return validatePhoneNumber(value);
      })
      .required('Phone is required')
  }),
  smart_post_hub_id: string().when('has_smartpost', {
    is: true,
    then: string().required('Select a FedEx SmartPost® Hub ID.'),
    otherwise: string().nullable()
  }),
  has_freight_account: boolean(),
  freight_account: mixed().when('has_freight_account', {
    is: true,
    then: () => freightAccountSchema,
    otherwise: () => object({}).optional().default(undefined)
  }),
  accept_terms: bool().oneOf([true], 'Must accept the FedEx End-User License Agreement.')
});

/** SmartPost Hub ID */
export const smartPostHubIds = [
  {value: 5185, label: 'ALPA Allentown'},
  {value: 5303, label: 'ATGA Atlanta'},
  {value: 5213, label: 'BAMD Baltimore'},
  {value: 5281, label: 'CHNC Charlotte'},
  {value: 5929, label: 'COCA Chino'},
  {value: 5751, label: 'DLTX Dallas'},
  {value: 5802, label: 'DNCO Denver'},
  {value: 5481, label: 'DTMI Detroit'},
  {value: 5087, label: 'EDNJ Edison'},
  {value: 5431, label: 'GCOH Grove City'},
  {value: 5771, label: 'HOTX Houston'},
  {value: 5436, label: 'GPOH Groveport Ohio'},
  {value: 5902, label: 'LACA Los Angeles'},
  {value: 5465, label: 'ININ Indianapolis'},
  {value: 5648, label: 'KCKS Kansas City'},
  {value: 5254, label: 'MAWV Martinsburg'},
  {value: 5183, label: 'MAPA Macungie'},
  {value: 5379, label: 'METN Memphis'},
  {value: 5552, label: 'MPMN Minneapolis'},
  {value: 5531, label: 'NBWI New Berlin'},
  {value: 5110, label: 'NENY Newburgh'},
  {value: 5095, label: 'NENJ Newark'},
  {value: 5015, label: 'NOMA Northborough'},
  {value: 5327, label: 'ORFL Orlando'},
  {value: 5194, label: 'PHPA Philadelphia'},
  {value: 5854, label: 'PHAZ Phoenix'},
  {value: 5150, label: 'PTPA Pittsburgh'},
  {value: 5958, label: 'SACA Sacramento'},
  {value: 5097, label: 'SBNJ South Brunswick'},
  {value: 5186, label: 'SCPA Scranton'},
  {value: 5843, label: 'SCUT Salt Lake City'},
  {value: 5983, label: 'SEWA Seattle'},
  {value: 5631, label: 'STMO St. Louis'},
  {value: 5893, label: 'RENV Reno'},
  {value: 5345, label: 'TAFL Tampa'},
  {value: 5602, label: 'WHIL Wheeling'},
  {value: 5061, label: 'WICT Windsor'}
];

/**
 * Fedex Account Registration Form
 * @param {*} props
 */
const FedexRegistrationForm = (props) => {
  const {values, defaultValues, validationSchema, onCancel, onSubmit, router, isEditing, ...formProps} = props;

  /** Form cancel callback  */
  const handleCancel = (values, actions) => {
    if (onCancel) {
      onCancel(values, actions);
    }
  };

  /** Form submit callback  */
  const handleSubmit = (values, actions) => {
    if (onSubmit) {
      onSubmit(values, actions);
    }
  };

  const handleAddFreightAccount = (values, setValues) => {
    setValues({
      ...values,
      has_freight_account: true,
      freight_account: {
        freight_account_number: '',
        line_1: '',
        city: '',
        state_or_province: '',
        postal_code: '',
        country_code: ''
      }
    });
  };

  return (
    <Formik
      {...formProps}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={{...defaultValues, ...values}}
    >
      {({handleSubmit, setFieldValue, isSubmitting, dirty, values, isValid, errors, setValues, ...props}) => {
        return (
          <form noValidate="novalidate" onSubmit={handleSubmit}>
            <div title="Main Account" className="border-spacing-3 rounded border-1 border-solid border-[#D8D8D8]">
              <div className="grid-col-1 grid-row-1 grid border-b-1 border-[#D8D8D8] p-4">
                <h5>Main Account</h5>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 grid-rows-1 gap-6 border-b-1 border-[#D8D8D8] pt-1 pb-4">
                  <Field
                    disabled={isEditing}
                    required
                    name="account_number"
                    label="Parcel Account Number"
                    component={FormikTextInput}
                  />
                  <Field name="account_nickname" label="Nickname" component={FormikTextInput} />
                </div>
                <div title="Parcel Contact Information" className="border-b-1 border-[#D8D8D8] pb-4">
                  <h5>Contact Information</h5>
                  <div className="grid grid-rows-3 gap-6">
                    <div className="inline-grid grid-cols-2 gap-6">
                      <Field
                        required
                        name="contact.first_name"
                        label="Contact First Name"
                        component={FormikTextInput}
                      />
                      <Field required name="contact.last_name" label="Contact Last Name" component={FormikTextInput} />
                    </div>
                    <div className="inline-grid grid-cols-2 gap-6">
                      <Field required name="contact.company_name" label="Company Name" component={FormikTextInput} />
                      <Field required name="contact.email_address" label="Email Address" component={FormikTextInput} />
                    </div>
                    <div className="inline-grid grid-cols-2 gap-6">
                      <Field name="secondary_email" label="Secondary Email Address" component={FormikTextInput} />
                      <Field required name="contact.phone_number" label="Phone Number" component={FormikPhoneInput} />
                    </div>
                  </div>
                </div>
                <div title="Parcel Billing Address" className="border-b-1 border-[#D8D8D8] pb-4">
                  <h5>Billing Address</h5>
                  <div className="grid grid-rows-3 gap-6">
                    <Field required name="billing_address.line_1" label="Street Address" component={FormikTextInput} />
                    <div className="inline-grid grid-cols-2 gap-6">
                      <Field required name="billing_address.city" label="City" component={FormikTextInput} />
                      <Field
                        simpleValue
                        required
                        clearable={false}
                        name="billing_address.state_or_province"
                        label="State"
                        options={states.map((e) => ({label: e.name, value: e.id}))}
                        component={FormikSelect}
                      />
                    </div>
                    <div className="inline-grid grid-cols-2 gap-6">
                      <Field
                        required
                        name="billing_address.postal_code"
                        label="Postal Code"
                        component={FormikTextInput}
                      />
                      <Field
                        simpleValue
                        required
                        clearable={false}
                        name="billing_address.country_code"
                        label="Country"
                        options={countries}
                        component={FormikSelect}
                      />
                    </div>
                  </div>
                </div>
                <div title="FedEx Smart Post">
                  <div>
                    <h5>FedEx SmartPost®</h5>
                    <p>Please enter FedEx SmartPost® Hub ID, if your account includes SmartPost.</p>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="col-span-1">
                      <Field
                        simpleValue
                        clearable={false}
                        name="smart_post_hub_id"
                        label="FedEx SmartPost® Hub ID"
                        getOptionLabel={({value, label}) => `${label} ${value}`}
                        options={smartPostHubIds}
                        component={FormikSelect}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="py-5">
              <DeprecatedButton
                role="button"
                variant="tertiary"
                icon={<SvgIcon name="AddCircleOutlined" />}
                onClick={() => handleAddFreightAccount(values, setValues)}
              >
                Add Freight Account
              </DeprecatedButton>
            </div>
            <div className={`${values.has_freight_account ? '' : 'collapse'}`}>
              <div title="Freight Account" className="border-spacing-3 rounded border-1 border-solid border-[#D8D8D8]">
                <div className="grid-col-1 grid-row-1 grid border-b-1 border-[#D8D8D8] p-4">
                  <h5>Freight Account</h5>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 border-b-1 border-[#D8D8D8] pt-1 pb-4">
                    <Field
                      required={values.has_freight_account}
                      name="freight_account.freight_account_number"
                      label="Freight Account Number"
                      component={FormikTextInput}
                    />
                  </div>
                  <div title="Freight Billing Address" className="pb-4">
                    <h5>Billing Address</h5>
                    <div className="grid grid-rows-3 gap-6">
                      <Field
                        required={values.has_freight_account}
                        name="freight_account.line_1"
                        label="Street Address"
                        component={FormikTextInput}
                      />
                      <div className="inline-grid grid-cols-2 gap-6">
                        <Field required name="freight_account.city" label="City" component={FormikTextInput} />
                        <Field
                          simpleValue
                          required={values.has_freight_account}
                          clearable={false}
                          name="freight_account.state_or_province"
                          label="State"
                          options={states.map((e) => ({label: e.name, value: e.id}))}
                          component={FormikSelect}
                        />
                      </div>
                      <div className="inline-grid grid-cols-2 gap-6">
                        <Field
                          required={values.has_freight_account}
                          name="freight_account.postal_code"
                          label="Postal Code"
                          component={FormikTextInput}
                        />
                        <Field
                          simpleValue
                          required={values.has_freight_account}
                          clearable={false}
                          name="freight_account.country_code"
                          label="Country"
                          options={countries}
                          component={FormikSelect}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="div-checkbox-field terms-checkbox">
              <Field
                name="accept_terms"
                label={
                  <span>
                    I accept the{' '}
                    <a href="http://www.fedex.com/us/developer/product/wsla_corp.pdf" target="_blank" rel="noreferrer">
                      FedEx End-User License Agreement
                    </a>
                    .
                  </span>
                }
                component={FormikCheckbox}
              />
            </div>
            <div className="fedex-copyright">
              <img src="/images/fedex_logo.svg" width="90" />
              <span>The FedEx service marks are owned by Federal Express Corporation and are used by permission.</span>
            </div>
            {dirty && (
              <FormFooter
                primaryActionName={isEditing ? 'Update FedEx Account' : 'Connect FedEx'}
                isValid={isValid}
                isSubmitting={isSubmitting}
                onCancel={handleCancel}
              />
            )}
          </form>
        );
      }}
    </Formik>
  );
};

FedexRegistrationForm.defaultProps = {
  defaultValues,
  validationSchema
};

export default FedexRegistrationForm;
